import {Injectable, Injector} from '@angular/core';
import {EventsService} from '@webmate-web-core/shared/services/events/events.service';
import {NavigationEnd, Router} from '@angular/router';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {WorkbenchLayoutManager} from '../../../workbench/shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../../workbench/shared/services/workbench-context.service';
import {WorkbenchCommandRegistry} from '../../../workbench/shared/services/workbench-command-registry.service';
import {Subject} from 'rxjs';
import {distinctUntilChanged, filter, throttleTime} from 'rxjs/operators';
import {ApiTrackingBusService} from '@webmate-web-core/shared/services/api-tracking-bus.service';
import {UserService} from '@webmate-web-core/shared/services/user.service';

export interface TrackingEvent {
    source: 'DOMClick' | 'WebmateEvent' | 'RouteChange' | 'ModalTool' | 'WorkbenchLayoutChange' | 'WorkbenchContextChange' | 'WorkbenchCommandRegistry' | 'ApiCall';
    additionalInfo?: any;
}

@Injectable()
export class TrackingEventService {
    private initialized = false;
    private events$ = new Subject<TrackingEvent>();
    private domClick$ = new Subject<MouseEvent>();
    private workbenchLayoutSubscribed = false;
    private workbenchContextSubscribed = false;
    private workbenchCommandSubscribed = false;
    private readonly RELEVANT_DOM_TAGS = new Set(['BUTTON', 'A', 'DIV', 'INPUT', 'SELECT', 'TEXTAREA']);

    // Add specific event types or prefixes that are relevant for tracking
    private readonly RELEVANT_WEBMATE_EVENT_TYPES = ['Device.', 'TestRun.'];

    constructor(private eventService: EventsService, private router: Router, private toolService: ModalToolContainerService,
                private injector: Injector, private apiTrackingBus: ApiTrackingBusService, private userService: UserService) {
    }

    init() {
        if (this.initialized) {
            return;
        }
        this.initialized = true;

        this.userService.getCurrentUserData().subscribe(user => {
            if (user!.publicProperties['trackingConsent'] === undefined) {
                return;
            }

            if (!user.publicProperties['trackingConsent']) {
                return;
            }


            this.domClick$.pipe(
                throttleTime(100, undefined, {leading: true, trailing: true}),
                filter(event => this.shouldTrackDomEvent(event)),
                distinctUntilChanged((a, b) => this.getDomEventKey(a) === this.getDomEventKey(b))
            ).subscribe(event => {
                const target = event.target as Element;
                this.events$.next({source: 'DOMClick', additionalInfo: {tagName: target.tagName, id: target.id, classes: target.className}});
            });

            // Setup Tracking for Webmate Events
            this.eventService.getAllEvents().pipe(filter(e => this.RELEVANT_WEBMATE_EVENT_TYPES.some(prefix => e.eventType.includes(prefix)))).subscribe(event => {
                this.events$.next({source: 'WebmateEvent', additionalInfo: {eventType: event.type, payload: event.payload}});
            });

            // Setup Tracking for Route Changes
            this.router.events.pipe(filter(e => e instanceof NavigationEnd))
                .subscribe((e: NavigationEnd) => {
                    this.events$.next({source: 'RouteChange', additionalInfo: {url: e.urlAfterRedirects}});
                });

            // Setup Tracking for Tools
            this.toolService.getEvents$().subscribe(event => {
                this.events$.next({source: 'ModalTool', additionalInfo: {event}});
            })

            // Setup Tracking for API calls
            this.apiTrackingBus.observe().subscribe(call => {
                this.events$.next({source: 'ApiCall', additionalInfo: call});
            });
        });
    }

    trackDomClick(event: MouseEvent) {
        this.domClick$.next(event);
    }

    getTrackingEvents() {
        return this.events$.asObservable();
    }

    registerWorkbenchSources(injector?: Injector) {
        const sourceInjector = injector ?? this.injector;

        if (!this.workbenchCommandSubscribed) {
            const commandRegistry = sourceInjector.get(WorkbenchCommandRegistry, null);
            if (commandRegistry) {
                this.workbenchCommandSubscribed = true;
                commandRegistry.subscribeToCommandExecutions$().subscribe(commandExecution => {
                    this.events$.next({source: 'WorkbenchCommandRegistry', additionalInfo: {commandExecution}});
                });
            }
        }

        if (!this.workbenchLayoutSubscribed) {
            const layoutManager = sourceInjector.get(WorkbenchLayoutManager, null);
            if (layoutManager) {
                this.workbenchLayoutSubscribed = true;
                layoutManager.subscribeActiveViewId$().subscribe(activeView => {
                    this.events$.next({source: 'WorkbenchLayoutChange', additionalInfo: {activeView}});
                });
                layoutManager.subscribeActiveContainerId$().subscribe(activeViewContainer => {
                    this.events$.next({source: 'WorkbenchLayoutChange', additionalInfo: {activeViewContainer}});
                });

            }
        }

        if (!this.workbenchContextSubscribed) {
            const contextService = sourceInjector.get(WorkbenchContextService, null);
            if (contextService) {
                this.workbenchContextSubscribed = true;
                contextService.subscribeWorkbenchContext$().subscribe(context => {
                    this.events$.next({source: 'WorkbenchContextChange', additionalInfo: {context}});
                });
            }
        }
    }

    private shouldTrackDomEvent(event: MouseEvent): boolean {
        const target = event.target as Element | null;
        if (!target) {
            return false;
        }
        return this.RELEVANT_DOM_TAGS.has(target.tagName);
    }

    private getDomEventKey(event: MouseEvent): string {
        const target = event.target as Element | null;
        if (!target) {
            return '';
        }
        const tagName = target.tagName;
        const id = target.id || '';
        const classes = typeof target.className === 'string' ? target.className : '';
        return `${tagName}#${id}.${classes}`;
    }
}
